import Dexie, { Table } from 'dexie';
import type { ConfigEntityDetail } from '../../types/entities';
import type { EnvironmentKey } from '../../types/authoring';
import { getRequiredDomainEntityType } from '../../utilities/get-required-domain-entity-type/get-required-domain-entity-type';

export type EntityBulkOperationStatus =
  | 'ready'
  | 'complete'
  | 'error'
  | 'skipped';

/**
 * A reference to another entity that this entity depends on.
 * Used to establish processing order - dependencies must be processed first.
 */
export interface EntityDependencyRef {
  id: string;
  domainEntityType: string;
}

export interface EntityIndexedDBEntry {
  id: string;
  domainEntityType: string;
  data: ConfigEntityDetail;
  status: EntityBulkOperationStatus;
  sourceTenantId?: string;
  /** Import operation specific fields */
  newId?: string;
  /** Promote operation specific fields */
  targetEnvironment?: EnvironmentKey;
  sourceEnvironment?: EnvironmentKey;
  /**
   * Entities that this entity depends on (references).
   * These must be processed/created before this entity.
   */
  dependencies?: EntityDependencyRef[];
  /**
   * Unix timestamp in milliseconds of the last update to this entry
   * (e.g. status change or data modification). Used to detect and clean up
   * stale or half-finished bulk operations.
   */
  lastUpdated?: number;
  /**
   * Error message if the entity failed during the bulk operation.
   * Only present when status is 'error'.
   */
  error?: string;
}

export class EntitiesDatabase extends Dexie {
  entities!: Table<EntityIndexedDBEntry, string>;

  constructor(name: string) {
    super(name);

    // IMPORTANT: when incrementing version, you MUST use an integer value
    this.version(1).stores({
      entities: 'id, domainEntityType, newId, status',
    });

    // Add compound index for efficient pending-import queries without scanning payloads
    this.version(2).stores({
      entities:
        'id, domainEntityType, newId, status, [domainEntityType+status]',
    });

    // Keep ImportEntitiesDatabase schema aligned with bulk-action reporting.
    this.version(3).stores({
      entities:
        'id, domainEntityType, newId, status, [domainEntityType+status]',
      bulkActionSessions: 'id',
    });
  }

  async upsertEntity(
    entry: Omit<EntityIndexedDBEntry, 'domainEntityType' | 'lastUpdated'>,
  ): Promise<void> {
    await this.entities.put({
      ...entry,
      domainEntityType: getRequiredDomainEntityType(
        entry.data,
        'entity upsert',
      ),
      lastUpdated: Date.now(),
    });
  }

  /**
   * Adds an array of entities initialized with the pending status
   */
  async bulkUpsertEntities(
    entities: ConfigEntityDetail[],
    sourceTenantId?: string,
  ): Promise<void> {
    const now = Date.now();
    await this.entities.bulkPut(
      entities.map((entity) => ({
        id: entity.id,
        data: entity,
        domainEntityType: getRequiredDomainEntityType(
          entity,
          'bulk entity upsert',
        ),
        status: 'ready' as const,
        sourceTenantId,
        lastUpdated: now,
      })),
    );
  }

  async getEntityEntryById(
    id: string,
  ): Promise<EntityIndexedDBEntry | undefined> {
    const entry = await this.entities.get(id);

    return entry;
  }

  async getEntityEntryByNewId(
    newId: string,
  ): Promise<EntityIndexedDBEntry | undefined> {
    const entry = await this.entities.where('newId').equals(newId).first();

    return entry;
  }

  async deleteEntity(id: string): Promise<void> {
    await this.entities.delete(id);
  }

  async clearAll(domainEntityType?: string): Promise<void> {
    await (domainEntityType
      ? this.entities
          .where('domainEntityType')
          .equals(domainEntityType)
          .delete()
      : this.entities.clear());
  }

  async getEntitiesByDomainEntityType(
    type: string,
  ): Promise<EntityIndexedDBEntry[]> {
    const entries = await this.entities
      .where('domainEntityType')
      .equals(type)
      .toArray();

    return entries;
  }

  private getPendingImportEntriesCollection(type: string) {
    return this.entities
      .where('[domainEntityType+status]')
      .anyOf([type, 'ready' as const], [type, 'error' as const]);
  }

  /**
   * Returns count of entities that are pending import for a given domain entity type.
   * (Pending import = status is 'ready' or 'error'.)
   */
  async getPendingImportCountByDomainEntityType(type: string): Promise<number> {
    return this.getPendingImportEntriesCollection(type).count();
  }

  /**
   * Returns IDs of entities that are pending import for a given domain entity type.
   * Uses a compound index so we can avoid loading full entity payloads.
   */
  async getPendingImportEntityIdsByDomainEntityType(
    type: string,
  ): Promise<string[]> {
    return this.getPendingImportEntriesCollection(type).primaryKeys();
  }

  /** Efficiently updates the status of an entity without re-writing the full payload. */
  async updateEntityStatusById(
    id: string,
    status: EntityBulkOperationStatus,
    error?: string,
  ): Promise<void> {
    const updated = await this.entities.update(id, {
      status,
      lastUpdated: Date.now(),
      ...(error && { error }),
    });
    if (updated === 0) {
      throw new Error(`Entity with id ${id} not found`);
    }
  }

  /**
   * Efficiently updates entity payload + newId without re-reading the existing entry.
   * Keeps existing status and sourceTenantId intact.
   */
  async updateEntityDataById(
    id: string,
    newData: ConfigEntityDetail,
  ): Promise<void> {
    const domainEntityType = getRequiredDomainEntityType(
      newData,
      'entity update',
    );

    /**
     * domainEntityType is treated as immutable for an entry; changing it would break
     * indexing/filtering assumptions. Fail fast if it ever differs.
     */
    const existing = await this.entities.get(id);
    if (!existing) {
      throw new Error(`Entity with id ${id} not found`);
    }
    if (existing.domainEntityType !== domainEntityType) {
      throw new Error(
        `domainEntityType cannot be changed for entity ${id} (existing: ${existing.domainEntityType}, next: ${domainEntityType})`,
      );
    }

    await this.entities.update(id, {
      data: newData,
      newId: newData.id,
      lastUpdated: Date.now(),
    });
  }
}
// Singleton instance for import operations
export const importEntitiesDB = new EntitiesDatabase('ImportEntitiesDatabase');
export const copyEntitiesToEnvironmentDB = new EntitiesDatabase(
  'CopyEntitiesToEnvironmentDatabase',
);
